'use client'

import { useMemo, useState } from 'react'
import {
  EntityListShell,
  Pagination,
  SearchInput,
  Spinner,
  StatusChip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@rr/ui'
import { useCompanyVehicles } from '@/modules/platform/hooks/usePlatformCompanies'

const PAGE_SIZE = 20

export default function VehiclesList({ companyId }: { companyId: string }) {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const filters = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      search: search.trim() || undefined,
    }),
    [page, search],
  )

  const { data, isLoading, isError, error } = useCompanyVehicles(companyId, filters)
  const items: any[] = (data as any)?.data?.items ?? []
  const total: number = (data as any)?.data?.pagination?.total ?? items.length

  return (
    <EntityListShell
      toolbar={
        <SearchInput
          value={search}
          onChange={(v) => {
            setSearch(v)
            setPage(1)
          }}
          placeholder="Search by plate, brand, model…"
          className="max-w-sm"
        />
      }
      empty={!isLoading && items.length === 0}
      emptyState={
        isError
          ? error instanceof Error
            ? error.message
            : 'Failed to load vehicles.'
          : 'No vehicles in this organization.'
      }
      pagination={
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          onChange={setPage}
        />
      }
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Plate</TableHead>
              <TableHead>Brand / Model</TableHead>
              <TableHead>Year</TableHead>
              <TableHead>Carrier</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((v) => (
              <TableRow key={v.id}>
                <TableCell className="font-medium text-foreground tnum">
                  {v.vehicleNumber ?? '—'}
                </TableCell>
                <TableCell className="text-sm">
                  {[v.brand, v.model].filter(Boolean).join(' ') || '—'}
                </TableCell>
                <TableCell className="text-sm tnum">
                  {v.productionYear ?? '—'}
                </TableCell>
                <TableCell className="text-sm">
                  {v.carrier?.name ?? '—'}
                </TableCell>
                <TableCell className="text-sm">
                  {v.transportType?.name ?? '—'}
                </TableCell>
                <TableCell>
                  <StatusChip status={(v.status ?? 'active') as any} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </EntityListShell>
  )
}
